import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Phone, PhoneOff, Loader2, AlertTriangle } from "lucide-react";

const DISPOSITIONS = [
  { key: "interested", label: "Interested", cls: "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" },
  { key: "callback", label: "Callback", cls: "border-amber-500/40 text-amber-400 hover:bg-amber-500/10" },
  { key: "not_now", label: "Not now", cls: "border-rose-500/40 text-rose-400 hover:bg-rose-500/10" },
  { key: "no_answer", label: "No answer", cls: "border-border text-muted-foreground hover:bg-secondary" },
];

// Twilio Voice click-to-call softphone. Requires Twilio Voice credentials on the backend.
const WAVE_BARS = [0, 1, 2, 3, 4, 5, 6, 7, 8];

export default function ClickToCall({ lead, onLogged }) {
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | initializing | ready | ringing | connected | ended | unconfigured | error
  const [seconds, setSeconds] = useState(0);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      try { callRef.current?.disconnect(); } catch (e) { console.warn("Call cleanup failed", e); }
      try { deviceRef.current?.destroy(); } catch (e) { console.warn("Device cleanup failed", e); }
    };
  }, []);

  const getToken = async () => {
    const { data } = await api.get("/twilio/token");
    return data.token;
  };

  const init = async () => {
    setStatus("initializing");
    setMsg("");
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setStatus("error");
      setMsg("Microphone access is required to place calls.");
      return null;
    }
    try {
      const { Device } = await import("@twilio/voice-sdk");
      const token = await getToken();
      const device = new Device(token, { tokenRefreshMs: 30000, logLevel: 1 });
      device.on("registered", () => setStatus("ready"));
      device.on("error", (e) => { setStatus("error"); setMsg(e.message); });
      device.on("tokenWillExpire", async () => {
        try { device.updateToken(await getToken()); } catch (e) { setMsg("Call session expired — reopen the dialer to keep calling."); }
      });
      await device.register();
      deviceRef.current = device;
      return device;
    } catch (e) {
      if (e.response?.status === 503) {
        setStatus("unconfigured");
        setMsg(e.response?.data?.detail || "Twilio Voice is not configured.");
      } else {
        setStatus("error");
        setMsg(e.message || "Failed to initialize dialer.");
      }
      return null;
    }
  };

  const startTimer = () => {
    setSeconds(0);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const call = async () => {
    if (!lead?.phone) { toast.error("Lead has no phone number"); return; }
    let device = deviceRef.current;
    if (!device) device = await init();
    if (!device) return;
    try {
      setStatus("ringing");
      const c = await device.connect({ params: { To: lead.phone } });
      callRef.current = c;
      c.on("accept", () => { setStatus("connected"); startTimer(); });
      c.on("ringing", () => setStatus("ringing"));
      c.on("disconnect", () => { clearInterval(timerRef.current); setStatus("ended"); });
      c.on("cancel", () => { clearInterval(timerRef.current); setStatus("ended"); });
      c.on("error", (e) => { clearInterval(timerRef.current); setStatus("error"); setMsg(e.message); });
    } catch (e) {
      setStatus("error");
      setMsg(e.message || "Call failed");
    }
  };

  const hangup = () => {
    try { callRef.current?.disconnect(); } catch (e) { console.warn("Hang up failed", e); }
    clearInterval(timerRef.current);
    setStatus("ended");
  };

  const logDisposition = async (key, label) => {
    try {
      await api.post("/calls/log", { lead_id: lead.id, disposition: key, notes: `Outcome: ${label}`, duration: seconds });
      toast.success(`Call logged: ${label}`);
      onLogged && onLogged();
    } catch (e) {
      console.error("Failed to log call disposition:", e);
      toast.error("Couldn't save the call outcome — please try again.");
      return;
    }
    setStatus("idle");
    setSeconds(0);
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const active = status === "connected" || status === "ringing";

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="click-to-call">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-primary">Softphone</p>
          <p className="text-lg font-semibold mt-1">{lead?.phone || "No number"}</p>
        </div>
        <span className="text-xs px-2 py-1 rounded-md bg-secondary capitalize text-muted-foreground" data-testid="call-status">
          {status}
        </span>
      </div>

      <div className="h-16 flex items-center justify-center my-4">
        {status === "connected" ? (
          <div className="flex items-end gap-1 h-10">
            {WAVE_BARS.map((n) => (
              <span key={n} className="wave-bar w-1.5 rounded-full bg-primary" style={{ height: "100%", animationDelay: `${n * 0.08}s` }} />
            ))}
          </div>
        ) : status === "connected" || status === "ringing" ? (
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        ) : (
          <span className="font-mono text-2xl tracking-widest text-muted-foreground">{status === "ended" ? mmss : "--:--"}</span>
        )}
      </div>

      {status === "connected" && (
        <p className="text-center font-mono text-xl mb-3 text-primary">{mmss}</p>
      )}

      {(status === "unconfigured" || status === "error") && msg && (
        <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-3">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{msg}</span>
        </div>
      )}

      <div className="flex gap-2">
        {!active ? (
          <Button onClick={call} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90" disabled={status === "initializing"} data-testid="click-to-call-button">
            {status === "initializing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Phone className="h-4 w-4 mr-2" /> Call</>}
          </Button>
        ) : (
          <Button onClick={hangup} variant="destructive" className="flex-1" data-testid="hangup-button">
            <PhoneOff className="h-4 w-4 mr-2" /> Hang up
          </Button>
        )}
      </div>

      {status === "ended" && (
        <div className="mt-3 space-y-2" data-testid="call-disposition">
          <p className="text-xs text-muted-foreground text-center">Call ended ({mmss}) — log the outcome:</p>
          <div className="grid grid-cols-2 gap-2">
            {DISPOSITIONS.map((d) => (
              <Button key={d.key} variant="outline" size="sm" className={d.cls} onClick={() => logDisposition(d.key, d.label)} data-testid={`disposition-${d.key}`}>
                {d.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
